/**
 * Pure state derivation for the quota soft-warn banner.
 *
 * Extracted from `ChatBox` so the visibility logic can be unit-tested
 * without rendering the 6k-line ChatBox tree. The banner is shown when
 * the higher of (daily%, weekly%) is ≥80% and <100% and the user has
 * not dismissed it this session. At 100% the existing `UsageLimitModal`
 * (M3) takes over, so this helper deliberately hides the banner.
 *
 * Welcome-bonus suppression (#2054): while a free-tier user has any
 * remaining welcome-bonus tokens, every recorded usage call is consumed
 * from the bonus pool BEFORE hitting the daily/weekly quota window. The
 * banner would fire incorrectly during this period (the user thinks
 * they're "using" their daily limit but the dailyUsed counter sits at 0),
 * so we suppress it entirely until the bonus is depleted.
 */

import type { QuotaWindow } from '@/common/components/UsageWarnBanner';

export interface QuotaResponse {
  dailyUsed: number;
  dailyLimit: number;
  weeklyUsed: number;
  weeklyLimit: number;
  /**
   * Remaining welcome-bonus tokens (#2054). Optional for back-compat with
   * older API responses that pre-date the field; missing is treated as 0.
   */
  welcomeBonusRemaining?: number;
}

export interface QuotaBannerState {
  show: boolean;
  pct: number;
  window: QuotaWindow;
}

export function deriveQuotaBannerState(
  quota: QuotaResponse | null | undefined,
  dismissed: boolean,
): QuotaBannerState {
  if (!quota) return { show: false, pct: 0, window: 'daily' };
  const dailyPct = quota.dailyLimit > 0
    ? Math.floor((quota.dailyUsed / quota.dailyLimit) * 100)
    : 0;
  const weeklyPct = quota.weeklyLimit > 0
    ? Math.floor((quota.weeklyUsed / quota.weeklyLimit) * 100)
    : 0;
  const pct = Math.max(dailyPct, weeklyPct);
  const window: QuotaWindow = dailyPct >= weeklyPct ? 'daily' : 'weekly';
  // Welcome-bonus suppression: while bonus > 0 the user is on free
  // welcome credit; banner would be confusing.
  const onWelcomeBonus = (quota.welcomeBonusRemaining ?? 0) > 0;
  const show = pct >= 80 && pct < 100 && !dismissed && !onWelcomeBonus;
  return { show, pct, window };
}
